// Meshtastic US_915 frequency slots, from the firmware (src/mesh/RadioInterface.cpp,
// read 2026-09-11):
//
//   numChannels = floor((freqEnd - freqStart) / (spacing + bw))
//   freq        = freqStart + bw/2 + channel_num * bw          (spacing is 0 for US)
//   channel_num = hash(channelName) % numChannels               when the slot is unset
//
// where hash is djb2 and channelName for the default channel is the preset's
// long display name (src/DisplayFormatters.cpp). Slots are shown 1-based, as
// the app does: LongFast hashes to 19, displayed as slot 20, at 906.875 MHz.

import type { Scheme } from '../scheme.ts'

export const US_915 = { freqStartHz: 902e6, freqEndHz: 928e6, spacingHz: 0 }

export interface MeshtasticPreset {
  id: string
  name: string
  /** The string the firmware hashes for this preset's default slot. */
  hashName: string
  bwHz: number
  sf: number
  /** Coding rate denominator: 5 for 4/5, 8 for 4/8. */
  cr: 5 | 8
}

/** From meshtastic.org/docs/overview/radio-settings, read 2026-09-11. */
export const MESHTASTIC_PRESETS: readonly MeshtasticPreset[] = [
  { id: 'short-turbo', name: 'Short Turbo', hashName: 'ShortTurbo', bwHz: 500e3, sf: 7, cr: 5 },
  { id: 'short-fast', name: 'Short Fast', hashName: 'ShortFast', bwHz: 250e3, sf: 7, cr: 5 },
  { id: 'short-slow', name: 'Short Slow', hashName: 'ShortSlow', bwHz: 250e3, sf: 8, cr: 5 },
  { id: 'medium-fast', name: 'Medium Fast', hashName: 'MediumFast', bwHz: 250e3, sf: 9, cr: 5 },
  { id: 'medium-slow', name: 'Medium Slow', hashName: 'MediumSlow', bwHz: 250e3, sf: 10, cr: 5 },
  { id: 'long-turbo', name: 'Long Turbo', hashName: 'LongTurbo', bwHz: 500e3, sf: 11, cr: 8 },
  { id: 'long-fast', name: 'Long Fast', hashName: 'LongFast', bwHz: 250e3, sf: 11, cr: 5 },
  { id: 'long-moderate', name: 'Long Moderate', hashName: 'LongMod', bwHz: 125e3, sf: 11, cr: 8 },
  { id: 'long-slow', name: 'Long Slow', hashName: 'LongSlow', bwHz: 125e3, sf: 12, cr: 8 },
]

/** The firmware's `hash()`: djb2 over the bytes, as an unsigned 32-bit value. */
export function djb2(text: string): number {
  let h = 5381
  for (const byte of new TextEncoder().encode(text)) h = (Math.imul(h, 33) + byte) >>> 0
  return h
}

export function slotCount(bwHz: number): number {
  return Math.floor((US_915.freqEndHz - US_915.freqStartHz) / (US_915.spacingHz + bwHz))
}

/** Center of 0-based slot `n` at bandwidth `bwHz`. */
export function slotCenterHz(bwHz: number, n: number): number {
  return US_915.freqStartHz + bwHz / 2 + n * (US_915.spacingHz + bwHz)
}

/** 0-based default slot for a preset, as the firmware picks it. */
export function defaultSlot(preset: MeshtasticPreset): number {
  return djb2(preset.hashName) % slotCount(preset.bwHz)
}

/** The bandwidths any preset uses, ascending. */
export const MESHTASTIC_BANDWIDTHS: readonly number[] = [...new Set(MESHTASTIC_PRESETS.map((p) => p.bwHz))].sort(
  (a, b) => a - b,
)

export function meshtasticSchemeId(bwHz: number): string {
  return `meshtastic-us915-${Math.round(bwHz / 1e3)}k`
}

/** The slot raster at one bandwidth, with each preset's default slot marked. */
export function meshtasticScheme(bwHz: number): Scheme {
  const n = slotCount(bwHz)
  const channels = []
  for (let slot = 0; slot < n; slot++) {
    channels.push({ label: String(slot + 1), centerHz: slotCenterHz(bwHz, slot), widthHz: bwHz })
  }
  const markers = MESHTASTIC_PRESETS.filter((p) => p.bwHz === bwHz).map((p) => ({
    label: `${p.name} (slot ${defaultSlot(p) + 1})`,
    hz: slotCenterHz(bwHz, defaultSlot(p)),
  }))
  return {
    id: meshtasticSchemeId(bwHz),
    name: `Meshtastic US ${bwHz / 1e3} kHz slots`,
    source: 'meshtastic/firmware src/mesh/RadioInterface.cpp',
    builtin: true,
    channels,
    bands: [],
    markers,
  }
}
